// Formatting helpers: a small, safe Markdown parser/renderer and French dates, durations, numbers.
// parseMarkdown() is pure (no DOM) and returns an AST; renderMarkdown() builds DOM nodes from it
// with createElement/createTextNode only — never innerHTML. Links are limited to http(s) and mailto.
import { t, MONTHS, MONTHS_SHORT, DAYS, DAYS_SHORT } from './i18n/fr.js';

// ---------------------------------------------------------------------------------------------
// Markdown — AST
//
// Block nodes:  root{children} · paragraph{children} · heading{level, children} · code{lang, text}
//               list{ordered, start, tight, items: item{checked: null|bool, children}[]}
//               blockquote{children} · table{align[], header: inline[][], rows: inline[][][]} · hr
// Inline nodes: text{value} · strong{children} · em{children} · del{children} · code{value}
//               link{href, title, children} · br

const MAX_INPUT = 200000;
const MAX_BLOCK_DEPTH = 10;
const MAX_INLINE_DEPTH = 8;
const SCAN_BUDGET = 2000000;

// Linear-time patterns only: the renderer shows agent output that may quote third-party text, so a
// crafted line must never make a regex backtrack quadratically (it would freeze the tab).
const RE_FENCE = /^( {0,3})(`{3,}|~{3,})(.*)$/;
const RE_LANG = /^[^\s`]*/;
const MAX_URL = 2048;
const MAX_TABLE_COLS = 64;
const MAX_TABLE_ROWS = 1000;
const RE_HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*))?$/;
const RE_HR = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const RE_LIST = /^( *)([-*+]|\d{1,9}[.)])(?:([ \t]+)(.*))?$/;
const RE_QUOTE = /^ {0,3}> ?(.*)$/;
const RE_TABLE_SEP = /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;
const RE_TASK = /^\[([ xX])\][ \t]+/;
const RE_PUNCT = /[!-/:-@[-`{-~]/;
const RE_WORD = /[\p{L}\p{N}]/u;
const RE_SPACE = /\s/;

const isBlank = (line) => /^[ \t]*$/.test(line);
const indentOf = (line) => line.length - line.replace(/^ +/, '').length;
const isOrdered = (marker) => /\d/.test(marker);

/**
 * Parses Markdown into a plain AST (pure; safe on any input; input capped at 200 000 chars).
 * @param {string} text
 * @returns {{type: 'root', children: object[]}}
 */
export function parseMarkdown(text) {
  const src = String(text ?? '').slice(0, MAX_INPUT)
    .replace(/\r\n?/g, '\n').replace(/\t/g, '    ').replace(/\u0000/g, '\uFFFD');
  const state = { budget: SCAN_BUDGET };
  return { type: 'root', children: parseBlocks(src.split('\n'), 0, state) };
}

/** A fence opening line: ``` or ~~~ (3+), then an info string (no backtick for ``` fences). */
function matchFence(line) {
  const m = RE_FENCE.exec(line);
  if (!m || (m[2][0] === '`' && m[3].includes('`'))) return null;
  return m;
}

/** Removes the optional closing run of "#" of an ATX heading (linear time). */
function stripClosingHashes(s) {
  let end = s.length;
  while (end > 0 && (s[end - 1] === ' ' || s[end - 1] === '\t')) end -= 1;
  let j = end;
  while (j > 0 && s[j - 1] === '#') j -= 1;
  if (j < end && (j === 0 || s[j - 1] === ' ' || s[j - 1] === '\t')) end = j;
  return s.slice(0, end).trim();
}

function startsBlock(line) {
  return !!matchFence(line) || RE_HEADING.test(line) || RE_HR.test(line) || RE_QUOTE.test(line);
}

function parseBlocks(lines, depth, state) {
  const out = [];
  let para = null;
  const flush = () => {
    if (!para) return;
    out.push({ type: 'paragraph', children: parseInline(para.join('\n'), 0, state) });
    para = null;
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (isBlank(line)) { flush(); i += 1; continue; }
    let m = matchFence(line);
    if (m) {
      flush();
      const r = readFence(lines, i, m);
      out.push(r.node);
      i = r.next;
      continue;
    }
    if (RE_HR.test(line)) { flush(); out.push({ type: 'hr' }); i += 1; continue; }
    m = RE_HEADING.exec(line);
    if (m) {
      flush();
      const content = stripClosingHashes(m[2] || '');
      out.push({ type: 'heading', level: m[1].length, children: parseInline(content, 0, state) });
      i += 1;
      continue;
    }
    if (depth < MAX_BLOCK_DEPTH && RE_QUOTE.test(line)) {
      flush();
      const r = readQuote(lines, i, depth, state);
      out.push(r.node);
      i = r.next;
      continue;
    }
    m = RE_LIST.exec(line);
    if (m && depth < MAX_BLOCK_DEPTH && (para === null || !isOrdered(m[2]) || parseInt(m[2], 10) === 1)) {
      flush();
      const r = readList(lines, i, depth, state);
      out.push(r.node);
      i = r.next;
      continue;
    }
    if (isTableStart(lines, i)) {
      flush();
      const r = readTable(lines, i, state);
      out.push(r.node);
      i = r.next;
      continue;
    }
    (para ||= []).push(line.trim());
    i += 1;
  }
  flush();
  return out;
}

function readFence(lines, start, m) {
  const indent = m[1].length;
  const fence = m[2];
  const word = RE_LANG.exec(m[3].trim())[0];
  const lang = /^[A-Za-z0-9_+#.-]{1,32}$/.test(word) ? word : '';
  const close = new RegExp(`^ {0,3}${fence[0] === '`' ? '`' : '~'}{${fence.length},}[ \\t]*$`);
  const body = [];
  let i = start + 1;
  while (i < lines.length && !close.test(lines[i])) {
    const l = lines[i];
    body.push(indentOf(l) >= indent ? l.slice(indent) : l.replace(/^ +/, ''));
    i += 1;
  }
  return { node: { type: 'code', lang, text: body.join('\n') }, next: Math.min(i + 1, lines.length) };
}

function readQuote(lines, start, depth, state) {
  const body = [];
  let i = start;
  while (i < lines.length) {
    const l = lines[i];
    const q = RE_QUOTE.exec(l);
    if (q) { body.push(q[1]); i += 1; continue; }
    if (!isBlank(l) && body.length && !isBlank(body[body.length - 1]) && !startsBlock(l) && !RE_LIST.test(l)) {
      body.push(l.trim()); // lazy continuation of the quoted paragraph
      i += 1;
      continue;
    }
    break;
  }
  return { node: { type: 'blockquote', children: parseBlocks(body, depth + 1, state) }, next: i };
}

function readItem(lines, start, m) {
  const markerIndent = m[1].length;
  const markerLen = m[2].length;
  const pad = (m[3] || '').length;
  const contentIndent = markerIndent + markerLen + (pad >= 1 && pad <= 4 ? pad : 1);
  const first = pad > 4 ? ' '.repeat(pad - 1) + (m[4] || '') : (m[4] || '');
  const content = [first];
  let j = start + 1;
  let blanks = 0;
  let sawInnerBlank = false;
  while (j < lines.length) {
    const l = lines[j];
    if (isBlank(l)) { blanks += 1; j += 1; continue; }
    if (indentOf(l) >= contentIndent) {
      if (blanks) sawInnerBlank = true;
      for (; blanks > 0; blanks -= 1) content.push('');
      content.push(l.slice(contentIndent));
      j += 1;
      continue;
    }
    if (RE_LIST.test(l)) break;
    if (blanks === 0 && !startsBlock(l) && !isTableStart(lines, j)) {
      content.push(l.trim()); // lazy continuation
      j += 1;
      continue;
    }
    break;
  }
  return { content, next: j, blanksAfter: blanks, sawInnerBlank };
}

function readList(lines, start, depth, state) {
  const first = RE_LIST.exec(lines[start]);
  const ordered = isOrdered(first[2]);
  const node = { type: 'list', ordered, start: ordered ? parseInt(first[2], 10) : 1, tight: true, items: [] };
  let i = start;
  let pendingBlank = false;
  while (i < lines.length) {
    const m = RE_LIST.exec(lines[i]);
    if (!m || isOrdered(m[2]) !== ordered) break;
    if (pendingBlank) node.tight = false;
    const r = readItem(lines, i, m);
    if (r.sawInnerBlank) node.tight = false;
    let checked = null;
    const tm = RE_TASK.exec(r.content[0]);
    if (tm) {
      checked = tm[1] !== ' ';
      r.content[0] = r.content[0].slice(tm[0].length);
    }
    node.items.push({ type: 'item', checked, children: parseBlocks(r.content, depth + 1, state) });
    i = r.next;
    pendingBlank = r.blanksAfter > 0;
    if (pendingBlank) {
      const nm = i < lines.length ? RE_LIST.exec(lines[i]) : null;
      if (!nm || isOrdered(nm[2]) !== ordered) break;
    }
  }
  return { node, next: i };
}

/** Splits a table row on unescaped pipes; strips the outer pipes. */
function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  const cells = [];
  let cur = '';
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (c === '\\' && s[i + 1] === '|') { cur += '|'; i += 1; continue; }
    if (c === '|') { cells.push(cur.trim()); cur = ''; continue; }
    cur += c;
  }
  cells.push(cur.trim());
  return cells;
}

function isTableStart(lines, i) {
  const head = lines[i];
  const sep = lines[i + 1];
  if (sep === undefined || !head.includes('|') || !RE_TABLE_SEP.test(sep)) return false;
  if (!sep.includes('|') && !head.trim().startsWith('|')) return false;
  const cols = splitRow(head).length;
  return cols <= MAX_TABLE_COLS && cols === splitRow(sep).length;
}

function readTable(lines, start, state) {
  const header = splitRow(lines[start]);
  const align = splitRow(lines[start + 1]).map((c) => {
    const l = c.startsWith(':');
    const r = c.endsWith(':');
    return l && r ? 'center' : r ? 'right' : l ? 'left' : null;
  });
  const rows = [];
  let i = start + 2;
  while (i < lines.length && rows.length < MAX_TABLE_ROWS && !isBlank(lines[i]) && lines[i].includes('|') && !startsBlock(lines[i])) {
    const cells = splitRow(lines[i]);
    const row = [];
    for (let c = 0; c < header.length; c += 1) row.push(parseInline(cells[c] ?? '', 0, state));
    rows.push(row);
    i += 1;
  }
  return {
    node: { type: 'table', align, header: header.map((c) => parseInline(c, 0, state)), rows },
    next: i,
  };
}

// ---------------------------------------------------------------------------------------------
// Markdown — inline

function runLength(src, i, ch) {
  let j = i;
  while (src[j] === ch) j += 1;
  return j - i;
}

function matchCode(src, i) {
  const run = runLength(src, i, '`');
  let j = i + run;
  while (j < src.length) {
    const k = src.indexOf('`', j);
    if (k < 0) return null;
    const r2 = runLength(src, k, '`');
    if (r2 === run) {
      let value = src.slice(i + run, k).replace(/\n/g, ' ');
      if (value.length > 2 && value.startsWith(' ') && value.endsWith(' ') && value.trim()) value = value.slice(1, -1);
      return { nodes: [{ type: 'code', value }], end: k + run };
    }
    j = k + r2;
  }
  return null;
}

/**
 * Returns a safe href for http(s)/mailto URLs, or null for anything else (javascript:, data:…).
 * @param {string} raw
 * @returns {string|null}
 */
export function safeUrl(raw) {
  const cleaned = String(raw ?? '').replace(/[\u0000-\u0020\u007f-\u009f]/g, '');
  if (!/^(https?:\/\/|mailto:)/i.test(cleaned)) return null;
  try {
    const u = new URL(cleaned);
    if (!['http:', 'https:', 'mailto:'].includes(u.protocol)) return null;
    if (u.protocol !== 'mailto:' && !u.hostname) return null;
    return u.href;
  } catch {
    return null;
  }
}

function unescapeMd(s) {
  return s.replace(/\\([!-/:-@[-`{-~])/g, '$1');
}

function stripLinks(nodes) {
  const out = [];
  for (const n of nodes) {
    if (n.type === 'link') out.push(...stripLinks(n.children));
    else if (n.children) out.push({ ...n, children: stripLinks(n.children) });
    else out.push(n);
  }
  return out;
}

function matchLink(src, i, depth, state, isImage) {
  let j = i + 1;
  let level = 1;
  while (j < src.length) {
    if (--state.budget < 0) return null;
    const c = src[j];
    if (c === '\\') { j += 2; continue; }
    if (c === '`') {
      const r = matchCode(src, j);
      if (r) { j = r.end; continue; }
    }
    if (c === '[') level += 1;
    else if (c === ']') { level -= 1; if (level === 0) break; }
    j += 1;
  }
  if (level !== 0 || src[j + 1] !== '(') return null;
  const textEnd = j;
  let k = j + 2;
  while (src[k] === ' ') k += 1;
  let url;
  // Every scan below is bounded (MAX_URL) so a run of unclosed "[a](" stays linear overall.
  if (src[k] === '<') {
    const e = src.slice(k, k + MAX_URL).indexOf('>');
    if (e < 0) return null;
    url = src.slice(k + 1, k + e);
    k += e + 1;
  } else {
    const s = k;
    let parens = 0;
    while (k < src.length) {
      if (k - s > MAX_URL || --state.budget < 0) return null;
      const c = src[k];
      if (c === '\\') { k += 2; continue; }
      if (c === '(') parens += 1;
      else if (c === ')') { if (parens === 0) break; parens -= 1; } else if (RE_SPACE.test(c)) break;
      k += 1;
    }
    url = src.slice(s, k);
  }
  while (src[k] === ' ' || src[k] === '\n') k += 1;
  let title = '';
  if (src[k] === '"' || src[k] === "'") {
    const q = src[k];
    const off = src.slice(k + 1, k + 1 + MAX_URL).indexOf(q);
    const e = off < 0 ? -1 : k + 1 + off;
    if (e < 0) return null;
    title = src.slice(k + 1, e);
    k = e + 1;
    while (src[k] === ' ') k += 1;
  }
  if (src[k] !== ')') return null;
  const label = src.slice(i + 1, textEnd);
  let children = stripLinks(parseInline(label, depth + 1, state));
  if (isImage && !children.length) children = [{ type: 'text', value: url }];
  const href = safeUrl(unescapeMd(url));
  const nodes = href
    ? [{ type: 'link', href, title: unescapeMd(title), children: children.length ? children : [{ type: 'text', value: href }] }]
    : children;
  return { nodes, end: k + 1 };
}

function matchAutolink(src, i) {
  const off = src.slice(i, i + MAX_URL).indexOf('>');
  if (off < 0) return null;
  const e = i + off;
  const inner = src.slice(i + 1, e);
  if (!inner || /[\s<]/.test(inner)) return null;
  const isMail = /^[^@\s:/]+@[^@\s]+\.[^@\s]+$/.test(inner);
  const href = safeUrl(isMail ? `mailto:${inner}` : inner);
  if (!href) return null;
  return { nodes: [{ type: 'link', href, title: '', children: [{ type: 'text', value: inner }] }], end: e + 1 };
}

function matchBareUrl(src, i) {
  if (i > 0 && /[\p{L}\p{N}_/@.:-]/u.test(src[i - 1])) return null;
  const m = /^https?:\/\/[^\s<>"]+/i.exec(src.slice(i, i + 2048));
  if (!m) return null;
  let end = m[0].length;
  let open = 0;
  let close = 0;
  for (let j = 0; j < end; j += 1) {
    if (m[0][j] === '(') open += 1;
    else if (m[0][j] === ')') close += 1;
  }
  // Trailing punctuation and unbalanced closing parentheses are not part of the URL (linear).
  for (;;) {
    const last = m[0][end - 1];
    if (/[.,;:!?'*_~\]]/.test(last)) end -= 1;
    else if (last === ')' && close > open) { end -= 1; close -= 1; } else break;
  }
  const url = m[0].slice(0, end);
  const href = safeUrl(url);
  if (!href || url.length < 10) return null;
  return { nodes: [{ type: 'link', href, title: '', children: [{ type: 'text', value: url }] }], end: i + url.length };
}

function flanking(src, pos, len) {
  const before = pos > 0 ? src[pos - 1] : ' ';
  const after = pos + len < src.length ? src[pos + len] : ' ';
  return {
    before, after,
    beforeSpace: RE_SPACE.test(before), afterSpace: RE_SPACE.test(after),
    beforePunct: RE_PUNCT.test(before), afterPunct: RE_PUNCT.test(after),
  };
}

function matchEmphasis(src, i, depth, state) {
  const ch = src[i];
  const run = runLength(src, i, ch);
  if (ch === '~' && run !== 2) return null;
  if (run > 3) return null;
  const L = ch === '~' ? 2 : run;
  const f = flanking(src, i, run);
  if (f.afterSpace) return null;
  if (ch === '_' && RE_WORD.test(f.before)) return null;
  let depthOpen = 0;
  let j = i + run;
  while (j < src.length) {
    if (--state.budget < 0) return null;
    const c = src[j];
    if (c === '\\') { j += 2; continue; }
    if (c === '`') {
      const r = matchCode(src, j);
      j = r ? r.end : j + runLength(src, j, '`');
      continue;
    }
    if (c !== ch) { j += 1; continue; }
    const r2 = runLength(src, j, ch);
    const g = flanking(src, j, r2);
    const canClose = !g.beforeSpace && (ch !== '_' || !RE_WORD.test(g.after));
    const canOpen = !g.afterSpace && (g.beforeSpace || g.beforePunct);
    if (canClose && depthOpen > 0) { depthOpen -= 1; j += r2; continue; }
    if (canClose && r2 >= L && j > i + run) {
      const inner = parseInline(src.slice(i + L, j), depth + 1, state);
      let node;
      if (ch === '~') node = { type: 'del', children: inner };
      else if (L === 1) node = { type: 'em', children: inner };
      else if (L === 2) node = { type: 'strong', children: inner };
      else node = { type: 'strong', children: [{ type: 'em', children: inner }] };
      return { nodes: [node], end: j + L };
    }
    if (canOpen) depthOpen += 1;
    j += r2;
  }
  return null;
}

function mergeText(nodes) {
  const out = [];
  for (const n of nodes) {
    const last = out[out.length - 1];
    if (n.type === 'text' && last && last.type === 'text') last.value += n.value;
    else if (n.type !== 'text' || n.value) out.push(n);
  }
  return out;
}

function parseInline(src, depth, state) {
  const out = [];
  let buf = '';
  const flush = () => { if (buf) { out.push({ type: 'text', value: buf }); buf = ''; } };
  const take = (r) => { flush(); out.push(...r.nodes); return r.end; };
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === '\\') {
      const n = src[i + 1];
      if (n === '\n') { flush(); out.push({ type: 'br' }); i += 2; continue; }
      if (n && RE_PUNCT.test(n)) { buf += n; i += 2; continue; }
      buf += c; i += 1; continue;
    }
    if (c === '\n') {
      buf = buf.replace(/ +$/, '');
      flush();
      out.push({ type: 'br' });
      i += 1;
      while (src[i] === ' ') i += 1;
      continue;
    }
    if (c === '`') {
      const r = matchCode(src, i);
      if (r) { i = take(r); continue; }
      const run = runLength(src, i, '`');
      buf += src.slice(i, i + run);
      i += run;
      continue;
    }
    if (depth < MAX_INLINE_DEPTH && state.budget > 0) {
      let r = null;
      if (c === '!' && src[i + 1] === '[') {
        r = matchLink(src, i + 1, depth, state, true);
      } else if (c === '[') {
        r = matchLink(src, i, depth, state, false);
      } else if (c === '<') {
        r = matchAutolink(src, i);
      } else if (c === '*' || c === '_' || c === '~') {
        r = matchEmphasis(src, i, depth, state);
        if (!r) { const run = runLength(src, i, c); buf += src.slice(i, i + run); i += run; continue; }
      } else if ((c === 'h' || c === 'H') && /^https?:\/\//i.test(src.slice(i, i + 8))) {
        r = matchBareUrl(src, i);
      }
      if (r) { i = take(r); continue; }
    }
    buf += c;
    i += 1;
  }
  flush();
  return mergeText(out);
}

// ---------------------------------------------------------------------------------------------
// Markdown — DOM rendering

function renderInline(nodes, parent, d, o = {}) {
  for (const n of nodes) {
    switch (n.type) {
      case 'text': parent.appendChild(d.createTextNode(n.value)); break;
      case 'br': parent.appendChild(d.createElement('br')); break;
      case 'code': {
        const el = d.createElement('code');
        el.textContent = n.value;
        parent.appendChild(el);
        break;
      }
      case 'strong': case 'em': case 'del': {
        const el = d.createElement(n.type);
        renderInline(n.children, el, d, o);
        parent.appendChild(el);
        break;
      }
      case 'link': {
        const href = safeUrl(n.href);
        if (!href) { renderInline(n.children, parent, d, o); break; }
        const a = d.createElement('a');
        a.setAttribute('href', href);
        a.setAttribute('rel', 'noopener noreferrer');
        a.setAttribute('target', '_blank');
        // Never the Markdown title: text written by an agent (possibly prompt-injected) could make
        // the tooltip, like the link text, imitate another address. The tooltip is the destination.
        a.setAttribute('title', href);
        const parts = [];
        collectText(n.children, parts);
        const label = parts.join('');
        // Empty or invisible text (zero-width characters) would hide the link: show the address.
        if (!visibleText(label)) a.appendChild(d.createTextNode(shownHref(href)));
        else renderInline(n.children, a, d, o);
        parent.appendChild(a);
        if (visibleText(label) && !sameDestination(label, href)) {
          // The text is not the address: say where the link goes (the host, or the whole address
          // with showHrefs — drafts and results that a person approves or forwards).
          const note = d.createElement('span');
          note.setAttribute('class', 'md-href');
          note.appendChild(d.createTextNode(` (${o.showHrefs ? shownHref(href) : hostOf(href)})`));
          parent.appendChild(note);
        }
        break;
      }
      default: break;
    }
  }
}

function renderBlock(b, d, tight, o = {}) {
  switch (b.type) {
    case 'paragraph': {
      if (tight) {
        const frag = d.createDocumentFragment();
        renderInline(b.children, frag, d, o);
        return frag;
      }
      const p = d.createElement('p');
      renderInline(b.children, p, d, o);
      return p;
    }
    case 'heading': {
      const el = d.createElement(`h${Math.min(6, Math.max(1, b.level))}`);
      renderInline(b.children, el, d, o);
      return el;
    }
    case 'code': {
      const pre = d.createElement('pre');
      const code = d.createElement('code');
      if (b.lang) code.setAttribute('class', `lang-${b.lang}`);
      code.textContent = b.text;
      pre.appendChild(code);
      return pre;
    }
    case 'hr': return d.createElement('hr');
    case 'blockquote': {
      const q = d.createElement('blockquote');
      for (const c of b.children) q.appendChild(renderBlock(c, d, false, o));
      return q;
    }
    case 'list': {
      const list = d.createElement(b.ordered ? 'ol' : 'ul');
      if (b.ordered && b.start !== 1) list.setAttribute('start', String(b.start));
      if (b.items.some((it) => it.checked !== null)) list.setAttribute('class', 'md-tasklist');
      for (const it of b.items) {
        const li = d.createElement('li');
        if (it.checked !== null) {
          const box = d.createElement('span');
          box.setAttribute('class', it.checked ? 'md-check is-checked' : 'md-check');
          box.setAttribute('aria-hidden', 'true');
          box.textContent = it.checked ? '☑' : '☐';
          li.appendChild(box);
          li.appendChild(d.createTextNode(' '));
        }
        it.children.forEach((c, idx) => {
          if (b.tight && idx > 0 && c.type === 'paragraph') li.appendChild(d.createElement('br'));
          li.appendChild(renderBlock(c, d, b.tight, o));
        });
        list.appendChild(li);
      }
      return list;
    }
    case 'table': {
      const wrap = d.createElement('div');
      wrap.setAttribute('class', 'md-table');
      const table = d.createElement('table');
      const thead = d.createElement('thead');
      const hr = d.createElement('tr');
      b.header.forEach((cell, idx) => {
        const th = d.createElement('th');
        th.setAttribute('scope', 'col');
        if (b.align[idx]) th.setAttribute('class', `md-align-${b.align[idx]}`);
        renderInline(cell, th, d, o);
        hr.appendChild(th);
      });
      thead.appendChild(hr);
      table.appendChild(thead);
      const tbody = d.createElement('tbody');
      for (const row of b.rows) {
        const tr = d.createElement('tr');
        row.forEach((cell, idx) => {
          const td = d.createElement('td');
          if (b.align[idx]) td.setAttribute('class', `md-align-${b.align[idx]}`);
          renderInline(cell, td, d, o);
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      wrap.appendChild(table);
      return wrap;
    }
    default: return d.createDocumentFragment();
  }
}

/** Invisible or blank characters (spaces, zero-width, bidi controls, fillers, variation selectors). */
const INVISIBLE_RE = /[\s\u00ad\u034f\u061c\u115f\u1160\u17b4\u17b5\u180b-\u180f\u200b-\u200f\u202a-\u202e\u2060-\u206f\u3164\ufe00-\ufe0f\ufeff\uffa0]/g;
const MAX_SHOWN_HREF = 500;

function visibleText(s) {
  return String(s ?? '').replace(INVISIBLE_RE, '');
}

function mailAddress(href) {
  const addr = href.slice('mailto:'.length).split('?')[0];
  try { return decodeURIComponent(addr); } catch { return addr; }
}

/** The address as shown on the page (a mailto link shows the e-mail address), capped. */
function shownHref(href) {
  const s = href.startsWith('mailto:') ? mailAddress(href) : href;
  return s.length > MAX_SHOWN_HREF ? `${s.slice(0, MAX_SHOWN_HREF)}…` : s;
}

/** The host of a link (the e-mail address for mailto). */
function hostOf(href) {
  if (href.startsWith('mailto:')) return shownHref(href);
  try { return new URL(href).host; } catch { return shownHref(href); }
}

/** Is the link text the destination itself (bare URL, autolink, text equal to the address)? */
function sameDestination(label, href) {
  const text = String(label ?? '').trim();
  if (!text) return false;
  if (text === href || safeUrl(text) === href) return true;
  return href.startsWith('mailto:') && text.toLowerCase() === mailAddress(href).toLowerCase();
}

/**
 * Renders Markdown into a DocumentFragment built with DOM APIs only (put it in a `.md` container).
 * A link whose text is not its address is followed by its destination (`span.md-href`): the host,
 * or the whole address with `showHrefs` (deliverables a person approves); a link without visible
 * text shows its address as text.
 * @param {string} text
 * @param {{doc?: Document, showHrefs?: boolean}} [opts] document to build with (tests pass a fake one)
 * @returns {DocumentFragment}
 */
export function renderMarkdown(text, { doc = globalThis.document, showHrefs = false } = {}) {
  const frag = doc.createDocumentFragment();
  const o = { showHrefs: Boolean(showHrefs) };
  for (const b of parseMarkdown(text).children) frag.appendChild(renderBlock(b, doc, false, o));
  return frag;
}

function collectText(nodes, out) {
  for (const n of nodes) {
    if (n.type === 'text' || n.type === 'code') out.push(n.value ?? '');
    else if (n.type === 'br') out.push(' ');
    else if (n.children) collectText(n.children, out);
  }
}

/**
 * Plain-text version of Markdown (for previews, titles, aria labels), whitespace collapsed.
 * @param {string} text
 * @param {number} [max] optional length cap (adds "…")
 */
export function markdownToText(text, max = 0) {
  const parts = [];
  for (const b of parseMarkdown(text).children) blockText(b, parts);
  const s = parts.join('').replace(/\s+/g, ' ').trim();
  return max ? truncate(s, max) : s;
}

function blockText(b, out) {
  if (b.type === 'code') out.push(b.text);
  else if (b.type === 'list') for (const it of b.items) for (const c of it.children) blockText(c, out);
  else if (b.type === 'blockquote') for (const c of b.children) blockText(c, out);
  else if (b.type === 'table') {
    for (const row of [b.header, ...b.rows]) for (const cell of row) { collectText(cell, out); out.push(' '); }
  } else if (b.children) collectText(b.children, out);
  out.push(' ');
}

// ---------------------------------------------------------------------------------------------
// Dates, times, durations (French, local time of the browser)

/** Two-digit zero padding. */
export function pad2(n) {
  return String(Math.trunc(n)).padStart(2, '0');
}

/**
 * Converts ms / ISO string / Date into a Date (null when missing or invalid).
 * @returns {Date|null}
 */
export function toDate(v) {
  if (v == null || v === '') return null;
  const d = v instanceof Date ? v : new Date(typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Whole calendar days between two instants (b − a), DST-safe. */
export function dayDiff(a, b) {
  const da = toDate(a);
  const db = toDate(b);
  if (!da || !db) return 0;
  return Math.round((startOfDay(db) - startOfDay(da)) / 86400000);
}

/** "08:30" (24 h). */
export function formatTime(v) {
  const d = toDate(v);
  return d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '';
}

/** "08:30:05". */
export function formatClock(v = Date.now()) {
  const d = toDate(v);
  return d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}` : '';
}

/**
 * "25 sept." · with weekday "ven. 25 sept." · the year is added when it is not the current one.
 * @param {*} v
 * @param {{weekday?: boolean, long?: boolean, year?: boolean, now?: number}} [opts]
 */
export function formatDate(v, opts = {}) {
  const d = toDate(v);
  if (!d) return '';
  const now = toDate(opts.now ?? Date.now());
  const month = (opts.long ? MONTHS : MONTHS_SHORT)[d.getMonth()];
  const day = d.getDate() === 1 ? t('date.first') : String(d.getDate());
  let s = `${day} ${month}`;
  if (opts.weekday) s = `${(opts.long ? DAYS : DAYS_SHORT)[d.getDay()]} ${s}`;
  if (opts.year || d.getFullYear() !== now.getFullYear()) s += ` ${d.getFullYear()}`;
  return s;
}

/** "aujourd'hui à 08:30" · "hier à 17:05" · "demain à 09:00" · "25 sept. à 08:30". */
export function formatDateTime(v, now = Date.now()) {
  const d = toDate(v);
  if (!d) return '';
  const diff = dayDiff(now, d);
  const time = formatTime(d);
  if (diff === 0) return t('date.todayAt', { time });
  if (diff === -1) return t('date.yesterdayAt', { time });
  if (diff === 1) return t('date.tomorrowAt', { time });
  if (diff > 1 && diff < 7) return t('date.dayAt', { day: DAYS[d.getDay()], time });
  return t('date.dateAt', { date: formatDate(d, { now }), time });
}

/**
 * Relative time in French. Past: "à l'instant", "il y a 5 min", "il y a 3 h", "hier à 17:05",
 * "il y a 4 j", then a date. Future: "dans 5 min", "aujourd'hui à 18:00", "demain à 08:00"…
 * short: compact form for dense lists ("5 min", "3 h", "hier", "4 j", "12 sept.").
 * @param {*} v
 * @param {number} [now]
 * @param {{short?: boolean}} [opts]
 */
export function relativeTime(v, now = Date.now(), opts = {}) {
  const d = toDate(v);
  if (!d) return '';
  const diff = now - d.getTime();
  const abs = Math.abs(diff);
  const min = Math.round(abs / 60000);
  const days = dayDiff(d, now);
  if (diff >= 0) {
    if (abs < 45000) return t('time.justNow');
    if (min < 60) return opts.short ? t('time.minShort', { n: Math.max(1, min) }) : t('time.minAgo', { n: Math.max(1, min) });
    const hours = Math.round(abs / 3600000);
    if (days === 0) return opts.short ? t('time.hourShort', { n: hours }) : t('time.hourAgo', { n: hours });
    if (days === 1) return opts.short ? t('time.yesterday') : t('date.yesterdayAt', { time: formatTime(d) });
    if (days < 7) return opts.short ? t('time.dayShort', { n: days }) : t('time.dayAgo', { n: days });
    return formatDate(d, { now });
  }
  if (abs < 45000) return t('time.soon');
  if (min < 60) return t('time.inMin', { n: Math.max(1, min) });
  if (opts.short && days === 0) return formatTime(d);
  return formatDateTime(d, now);
}

/**
 * Duration in French: "45 s", "1 min 20 s", "12 min", "2 h 05", "3 j 4 h".
 * @param {number} ms
 */
export function formatDuration(ms) {
  const n = Math.max(0, Math.round(Number(ms) || 0) / 1000);
  const s = Math.round(n);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 10) return s % 60 ? `${m} min ${pad2(s % 60)} s` : `${m} min`;
  if (m < 60) return `${m} min`;
  const hTotal = Math.floor(m / 60);
  if (hTotal < 24) return `${hTotal} h ${pad2(m % 60)}`;
  const dd = Math.floor(hTotal / 24);
  return hTotal % 24 ? `${dd} j ${hTotal % 24} h` : `${dd} j`;
}

/** Value for <input type="datetime-local"> in local time ("2026-09-25T08:30"). */
export function toLocalInputValue(v) {
  const d = toDate(v);
  if (!d) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Parses a datetime-local value as local time → ms (null when invalid). */
export function fromLocalInputValue(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(s || ''));
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

// ---------------------------------------------------------------------------------------------
// Numbers, text, colors

/** "12 345" with a narrow no-break space; decimals with a comma. */
export function formatNumber(n, decimals = 0) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const fixed = Math.abs(v).toFixed(decimals);
  const [int, dec] = fixed.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
  return `${v < 0 ? '−' : ''}${grouped}${dec ? `,${dec}` : ''}`;
}

/** "12 Ko", "3,4 Mo". */
export function formatBytes(bytes) {
  const b = Math.max(0, Number(bytes) || 0);
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${formatNumber(b / 1024, b < 10240 ? 1 : 0)} Ko`;
  return `${formatNumber(b / 1048576, b < 10485760 ? 1 : 0)} Mo`;
}

/** "0,42 $". */
export function formatCost(usd) {
  return `${formatNumber(usd, Number(usd) < 10 ? 2 : 0)} $`;
}

/** Percentage label "42 %". */
export function formatPercent(p) {
  return `${Math.round(Math.max(0, Math.min(100, Number(p) || 0)))} %`;
}

/** Cuts `s` to `n` chars with an ellipsis. */
export function truncate(s, n) {
  const str = String(s ?? '');
  return str.length > n ? `${str.slice(0, Math.max(0, n - 1)).trimEnd()}…` : str;
}

/** "Nicolas Maeck" → "NM"; "assistant" → "AS". */
export function initials(name) {
  const words = String(name ?? '').replace(/^claude\.ai\s+/i, '').trim().split(/[\s._-]+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/** Stable hue 0–359 from a string (for monograms and avatars). */
export function hashHue(str) {
  let h = 2166136261;
  for (const c of String(str ?? '')) { h ^= c.codePointAt(0); h = Math.imul(h, 16777619); }
  return Math.abs(h) % 360;
}

/** The palette keys of SPEC §11. */
export const PALETTE = ['coral', 'teal', 'sky', 'sand', 'lavender', 'periwinkle', 'sage', 'peach'];

/**
 * CSS color for a department/floor `color` (palette key or #hex); unknown → a palette color
 * derived from `fallbackSeed`.
 */
export function deptColor(color, fallbackSeed = '') {
  const c = String(color ?? '').trim();
  if (PALETTE.includes(c)) return `var(--${c})`;
  if (/^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(c)) return c;
  return `var(--${PALETTE[hashHue(fallbackSeed || c) % PALETTE.length]})`;
}

/** Short display name for a connector ("claude.ai Gmail" → "Gmail"). */
export function connectorLabel(name) {
  return String(name ?? '').replace(/^claude\.ai\s+/i, '').trim() || String(name ?? '');
}

/** French plural helper: plural(2, 'agent', 'agents') → "agents" (0 and 1 are singular). */
export function plural(n, one, other) {
  return Math.abs(Number(n) || 0) < 2 ? one : other;
}
